// mvp-returns Phase H11 — Damaged Inventory Turnover Analytics page.
// Shows per-category avg days in category / avg days to dispose, plus a
// configurable slow-movers list (items un-disposed for >= threshold days).

import { Anchor, NumberInput, Stack, Table, Text } from "@mantine/core";
import { EmptyStateGuide, HelpIcon, i18n, useHelpContext } from "@dehub/theme";
import { ClockIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { DamagedInventoryApi } from "../../../apis";
import { DeliveryRowAction } from "../../../components/delivery/action-controls";
import { DotPill, TableFrame } from "../../../components/delivery/atoms";
import type { Tone } from "../../../components/delivery/atoms";
import { PageLayout } from "../../../components/layout";
import { DetailCard } from "../../../components/return/detail-card";
import { ListState } from "../../../components/ui";
import { dateFormat } from "../../../utils/date";
import { bucketLabel } from "../../damaged-inventory";

const DEFAULT_THRESHOLD = 60;

/** @dehub/help key namespace of this screen (spec 12 §0). */
const HELP = "order.damaged-inventory-turnover";

// Lining figures so numbers align down a column.
const TNUM = { fontVariantNumeric: "tabular-nums" } as const;

// ─── Days-in-category tone ───────────────────────────────────────────────────

function daysTone(days: number): Tone {
  if (days > 180) return "red";
  if (days > 90) return "amber";
  return "grey";
}

// ─── Breadcrumb ──────────────────────────────────────────────────────────────

const breadcrumb = () => [
  { to: "/", label: i18n.t("returns:breadcrumb.home") },
  { to: "/returns", label: i18n.t("returns:breadcrumb.returns") },
  { to: "/damaged-inventory", label: i18n.t("returns:damagedTurnover.breadcrumbDamaged") },
  { label: i18n.t("returns:damagedTurnover.breadcrumbSlowMovers") },
];

// ─── Page ────────────────────────────────────────────────────────────────────

export function DamagedTurnoverPage() {
  const { t } = useTranslation(["returns", "inbound"]);
  const [thresholdDays, setThresholdDays] = useState<number | string>(DEFAULT_THRESHOLD);

  const thresholdNum = typeof thresholdDays === "number" ? thresholdDays : DEFAULT_THRESHOLD;

  // One call: the four category aggregates and the slow-movers list (seller-scoped by the BE).
  const { data, error, isLoading, mutate } = useSWR(
    ["damaged-turnover", thresholdNum],
    () => DamagedInventoryApi.turnover({ slowMoverThresholdDays: thresholdNum }),
    { revalidateOnFocus: false, keepPreviousData: true }
  );

  const buckets = data?.buckets ?? [];
  const slowMovers = data?.slowMovers ?? [];
  // The BE returns four zero rows when nothing was received yet: treat that as «no data».
  const hasBucketData = buckets.some((b) => b.activeRows > 0 || b.disposedRows > 0);
  const daysValue = (n: number) => t("returns:damagedTurnover.daysValue", { count: n });

  // @dehub/help: slow movers drive the drawer's «Одоо» card.
  const slowCount = slowMovers.length;
  const { set: setHelpContext } = useHelpContext();
  useEffect(() => {
    setHelpContext({ vars: { slowCount } });
  }, [slowCount, setHelpContext]);

  return (
    <PageLayout
      title={t("returns:damagedTurnover.title")}
      subTitle={t("returns:damagedTurnover.subtitle")}
      breadcrumb={breadcrumb()}
      loading={isLoading && !data}
    >
      <Stack gap="md">

        {/* ── Threshold control ── */}
        <NumberInput
          label={<>{t("returns:damagedTurnover.thresholdLabel")}<HelpIcon helpKey={`${HELP}.field.threshold`} /></>}
          description={t("returns:damagedTurnover.thresholdDesc")}
          value={thresholdDays}
          onChange={setThresholdDays}
          min={7}
          max={365}
          styles={{ wrapper: { width: "8.75rem" } }}
        />

        {error && !data ? (
          <TableFrame>
            <ListState state="error" onRetry={() => mutate()} errorCode={(error as { statusCode?: number })?.statusCode} />
          </TableFrame>
        ) : (
          <>
            {/* ── Category aggregate table ── */}
            <DetailCard title={t("returns:damagedTurnover.bucketAvgTitle")} helpKey={`${HELP}.section.bucket-avg`}>
              {!hasBucketData ? (
                <EmptyStateGuide
                  helpKey={`${HELP}.empty.main`}
                  icon={<ClockIcon size={24} />}
                  fallback={
                    <ListState
                      state="empty"
                      icon={<ClockIcon size={24} />}
                      title={t("returns:damagedTurnover.noData")}
                      description={t("returns:damagedTurnover.noDataHint")}
                    />
                  }
                />
              ) : (
                <TableFrame>
                  <Table.ScrollContainer minWidth={720}>
                    <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t("returns:damagedTurnover.col.bucket")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.activeRows")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.activeQty")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.avgDaysBucket")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.disposedRows")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.disposedQty")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.avgDaysDispose")}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {buckets.map((b) => (
                          <Table.Tr key={b.inventoryImpact}>
                            <Table.Td><Text size="sm" fw={500}>{bucketLabel(t, b.inventoryImpact)}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.activeRows}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.activeQuantity}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.activeRows > 0 ? daysValue(b.avgDaysInBucket) : "-"}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.disposedRows}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.disposedQuantity}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{b.disposedRows > 0 ? daysValue(b.avgDaysToDispose) : "-"}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </TableFrame>
              )}
            </DetailCard>

            {/* ── Slow-movers list ── */}
            <DetailCard title={t("returns:damagedTurnover.slowMoversTitle", { count: thresholdNum })} helpKey={`${HELP}.section.slow-movers`}>
              {slowMovers.length === 0 ? (
                <EmptyStateGuide
                  helpKey={`${HELP}.empty.main`}
                  when="slow"
                  icon={<ClockIcon size={24} />}
                  fallback={
                    <ListState
                      state="empty"
                      icon={<ClockIcon size={24} />}
                      title={t("returns:damagedTurnover.slowEmpty")}
                      description={t("returns:damagedTurnover.slowEmptyHint")}
                    />
                  }
                />
              ) : (
                <TableFrame>
                  <Table.ScrollContainer minWidth={900}>
                    <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md" styles={{ td: { verticalAlign: "middle" } }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th>{t("returns:damagedTurnover.col.action")}</Table.Th>
                          <Table.Th>{t("returns:damagedTurnover.col.product")}</Table.Th>
                          <Table.Th>{t("returns:damagedTurnover.col.sku")}</Table.Th>
                          <Table.Th>{t("returns:damagedTurnover.col.bucket")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.qty")}</Table.Th>
                          <Table.Th ta="right">{t("returns:damagedTurnover.col.days")}</Table.Th>
                          <Table.Th>{t("returns:damagedTurnover.col.receivedAt")}</Table.Th>
                          <Table.Th>{t("returns:breadcrumb.returns")}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {slowMovers.map((s) => (
                          <Table.Tr key={s.id}>
                            <Table.Td>
                              {/* O161 — the discount-sale workflow does not exist yet: visible, aria-disabled, reason «Удахгүй». */}
                              <DeliveryRowAction
                                label={t("returns:damagedTurnover.sellDiscount")}
                                icon={<ClockIcon />}
                                state={{ allowed: false, reason: t("returns:damagedTurnover.sellSoon") }}
                                onClick={() => undefined}
                              />
                            </Table.Td>
                            <Table.Td maw={260}><Text size="sm" fw={500} lineClamp={1}>{s.productName ?? s.sku ?? "-"}</Text></Table.Td>
                            <Table.Td><Text size="sm" ff="monospace" fz="var(--nw-fz-mono)" translate="no">{s.sku ?? "-"}</Text></Table.Td>
                            <Table.Td><Text size="sm">{bucketLabel(t, s.inventoryImpact)}</Text></Table.Td>
                            <Table.Td ta="right"><Text size="sm" ff="monospace" style={TNUM}>{s.quantity}</Text></Table.Td>
                            <Table.Td ta="right">
                              <DotPill tone={daysTone(s.daysInBucket)}>{daysValue(Number(s.daysInBucket.toFixed(0)))}</DotPill>
                            </Table.Td>
                            <Table.Td><Text size="sm" ff="monospace" style={TNUM}>{dateFormat(s.receivedAt)}</Text></Table.Td>
                            <Table.Td>
                              {s.returnNumber ? (
                                <Anchor component={Link} to={`/returns/${s.returnRequestId}`} ff="monospace" fz="var(--nw-fz-mono)" c="var(--nw-text)" translate="no">
                                  {s.returnNumber}
                                </Anchor>
                              ) : <Text size="sm">-</Text>}
                            </Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Table.ScrollContainer>
                </TableFrame>
              )}
            </DetailCard>
          </>
        )}
      </Stack>
    </PageLayout>
  );
}
